import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
/* Logs the user out after a window of no input activity. A reload does
 * NOT count as activity (the refresh cookie is HttpOnly so the user
 * stays signed in across reloads) — we only watch for genuine UI events.
 *
 * The configured threshold is 1h 24m (84min, or 5,040,000 ms) per the
 * product spec. Bump ``IDLE_MS`` if compliance tightens the rule.
 */
const IDLE_MS = 84 * 60 * 1000;
// We don't want to install a listener that fires on every mouse move,
// so we sample timestamps at this granularity instead.
const THROTTLE_MS = 5_000;
export function useIdleTimeout() {
    const navigate = useNavigate();
    const logout = useAuthStore((s) => s.logout);
    const lastActivity = useRef(Date.now());
    const lastTickPushed = useRef(0);
    useEffect(() => {
        function bump() {
            const now = Date.now();
            if (now - lastTickPushed.current < THROTTLE_MS)
                return;
            lastTickPushed.current = now;
            lastActivity.current = now;
        }
        const events = [
            'mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'wheel', 'visibilitychange',
        ];
        events.forEach((e) => document.addEventListener(e, bump, { passive: true }));
        const timer = window.setInterval(async () => {
            if (Date.now() - lastActivity.current < IDLE_MS)
                return;
            // Fire-and-forget the server-side logout so the refresh cookie is
            // invalidated; the local state is reset regardless.
            try {
                await api.post('/auth/logout/');
            }
            catch { /* offline, ignore */ }
            logout();
            navigate('/login', {
                replace: true,
                state: { idleTimeout: true },
            });
        }, 60_000);
        return () => {
            events.forEach((e) => document.removeEventListener(e, bump));
            window.clearInterval(timer);
        };
    }, [logout, navigate]);
}
